import { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

import Navbar from "./components/NavBar/navBar.js";
import Footer from "./components/Footer/Footer.js";
import TermsPage from "./components/Terms/TermsPage.js";
import HomePage from "./pages/Home/HomePage.js";
import GuidePage from "./pages/Guide/GuidePage.js";
import HelpTopicPage from "./pages/Help/HelpTopicPage";
import AboutPage from "./pages/About/AboutPage";
import OrganizersPage from "./pages/Organizers/OrganizersPage";
import FaqPage from "./pages/Faq/FaqPage";
import DocsPage from "./pages/Docs/DocsPage";
import HelpCenterPage from "./pages/Help/HelpCenterPage.js";
import ApproPage from "./pages/Appro/ApproPage";
import CharacterPage from "./pages/Character/CharacterPage";
import WorldMapPage from "./pages/World/WorldMapPage";
import { LanguageProvider } from "./i18n";
import PrivacyPolicy from "./components/Privacy/PrivacyPage.js";
import EventDetailsPage from "./components/EventPage/EventDetailsPage.js";
import UserProfilePage from "./components/UserProfile/UserProfile.js";
import DownloadAppPage from "./pages/Tester/DownloadAppPage.js";
import ChatRoomPage from "./components/ChatRoom/ChatRoom.js";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const previousPath = useRef(pathname);

  useEffect(() => {
    // Links like /features#multiple-checkpoints jump to that part of the page.
    // A new page starts at the top; changes within a page (e.g. switching help tabs) don't scroll.
    const target = hash && document.getElementById(hash.slice(1));
    if (target) {
      target.scrollIntoView();
    } else if (previousPath.current !== pathname) {
      window.scrollTo(0, 0);
    }
    previousPath.current = pathname;
  }, [pathname, hash]);

  return null;
};

function App() {
  return (
    <LanguageProvider>
      <div className="app-main-layout">
        <Router>
          <ScrollToTop />
          <Navbar />
          <main className="main-content">
            <Routes>
              <Route path="/" exact element={<HomePage />} />
              <Route
                path="/features"
                element={<GuidePage key="features" page="features" />}
              />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/organizers" element={<OrganizersPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              {/* One page per help topic (see content/help.json) */}
              <Route path="/help/:topicId" element={<HelpTopicPage />} />
              {/* The docs-style help center, alongside /help (content/docs.json) */}
              <Route path="/help-docs" element={<DocsPage />} />
              <Route
                path="/help-docs/:categoryId/:pageId"
                element={<DocsPage />}
              />
              <Route path="/character" element={<CharacterPage />} />
              <Route path="/map3d" element={<WorldMapPage />} />
              {/* One page per Appro event, with its map (see content/appro.json) */}
              <Route path="/appro/:slug" element={<ApproPage />} />
              {/* Old per-role help pages now open the matching help center tab. */}
              {[
                ["participants", "attending"],
                ["organizers", "organizing"],
                ["co-hosts", "organizing"],
                ["checkpoint-hosts", "organizing"],
              ].map(([role, tab]) => (
                <Route
                  key={role}
                  path={`/help/${role}`}
                  element={<Navigate to={`/help?tab=${tab}`} replace />}
                />
              ))}

              {/* Old "How it works" links */}
              <Route
                path="/how-it-works"
                element={<Navigate to="/features" replace />}
              />
              <Route
                path="/how-it-works/students"
                element={<Navigate to="/help?tab=attending" replace />}
              />
              <Route
                path="/how-it-works/organizers"
                element={<Navigate to="/features" replace />}
              />

              <Route path="/download" element={<DownloadAppPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />

              {/* handles deep linking */}
              <Route path="/events/:eventId" element={<EventDetailsPage />} />
              <Route
                path="/userprofile/:userId"
                element={<UserProfilePage />}
              />
              <Route path="/messages/:chatRoomId" element={<ChatRoomPage />} />
              <Route
                path="/events/:eventId/invitations/:invitationId/e-ticket"
                element={<EventDetailsPage />}
              />
              <Route
                path="/events/:eventId/locations/:locationId/self-check-in"
                element={<EventDetailsPage />}
              />
            </Routes>
          </main>
          <Footer />
        </Router>
      </div>
    </LanguageProvider>
  );
}

export default App;
